import React from 'react';
import PropTypes from 'prop-types';
import { noop } from 'underscore';
import { InlineChart } from '@bingads-webui-campaign-react/inline-chart';
import { InlineChartService } from '@bingads-webui-campaign/inline-chart-service';
import { InlineChartPreferences } from '@bingads-webui-campaign/inline-chart-preferences';
import { FluctuationService } from '@bingads-webui-campaign/fluctuation-service';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';

/**
 * Creates an inline chart with an accompanying data source and set of chart options.
 */
export class InlineChartWrapper extends React.PureComponent {
  static propTypes = {
    /**
     * grid-preferences adapter.
     */
    adapter: PropTypes.shape({
      registerSubadapter: PropTypes.func,
    }).isRequired,
    /**
     * Used for adding a date change listener.
     */
    addDateChangeListener: PropTypes.func.isRequired,
    /**
     * Entity data model.
     */
    edm: PropTypes.shape({
      currentAccount: PropTypes.any,
      currentCustomer: PropTypes.any,
    }).isRequired,

    /**
     * New data range as props for inline-chart whenever there is a date range update
     */
    dateRange: PropTypes.shape({}),
    /**
     * Type of entity to fetch chart data for.
     */
    entityType: PropTypes.string,
    /**
     * Filter object.
     */
    filter: PropTypes.shape({
      odataV4: PropTypes.func,
    }),
    /**
     * i18n
     * */
    i18n: PropTypes.shape({
      getString: PropTypes.func,
      formatDate: PropTypes.func,
    }).isRequired,
    /**
     * Path to use for inline images like fluctuation-bulb.png.
     */
    imagePath: PropTypes.string,
    /**
     * instrumentation component.
     */
    instrumentation: PropTypes.shape({
      addScenario: PropTypes.func,
      inlineChart: PropTypes.shape({
        create: PropTypes.func,
      }),
    }).isRequired,
    /**
     * True if Fluctuations feature is enabled.
     */
    isFluctuationsEnabled: PropTypes.bool,
    IsFluctuationExperimentEnabled: PropTypes.bool,
    /**
     * True if chart should be visible.
     */
    isShowChart: PropTypes.bool.isRequired,

    /**
     * True if chart panel should be hidden.
     */
    hideChartPanel: PropTypes.bool,

    /**
     * jsdata resource used for fetching chart data.
     */
    jsdata: PropTypes.shape({
      PerformanceTimeSeries: PropTypes.shape({
        findAll: PropTypes.func,
      }),
    }).isRequired,
    /**
     * PageContext component.
     */
    pageContext: PropTypes.shape({
      CurrentCustomer: PropTypes.shape({
        Id: PropTypes.number,
      }),
      CurrentCampaign: PropTypes.shape({
        Id: PropTypes.number,
      }),
      CurrentAccount: PropTypes.shape({
        Id: PropTypes.number,
      }),
      CurrentUser: PropTypes.shape({
        Id: PropTypes.number,
      }),
      CurrentTabId: PropTypes.string,
    }).isRequired,
    /**
     * config component.
     */
    pathConfig: PropTypes.shape({
      get: PropTypes.func,
    }).isRequired,
    /**
     * preferenceService component.
     */
    preferenceService: PropTypes.shape({}).isRequired,
    /**
     * Resize window change listener to set highchart container width.
     */
    resizeChartListener: PropTypes.func.isRequired,
    resizeEventHandler: PropTypes.func,
    /**
     * state component.
     */
    state: PropTypes.shape({}).isRequired,
    /**
     * grid-preferences store.
     */
    store: PropTypes.shape({}).isRequired,
    /**
     * Auth token used by Fluctuation service.
     */
    token: PropTypes.string,
    /**
     * Theme colors, can be null
     */
    seriesColors: PropTypes.shape({
      primary: PropTypes.shape({
        original: PropTypes.string,
        comparative: PropTypes.string,
      }),
      secondary: PropTypes.shape({
        original: PropTypes.string,
        comparative: PropTypes.string,
      }),
    }),

    /**
     * Current gridCriterion Type
     */
    gridCriterionType: PropTypes.string,

    perfMarker: perfMarkerPropTypes,

    serverContext: PropTypes.shape({}),

    gridDataSource: PropTypes.shape({}),

    defaultPrimaryMetric: PropTypes.number,
    scope: PropTypes.shape({}),
  };

  static defaultProps = {
    entityType: null,
    filter: null,
    imagePath: null,
    isFluctuationsEnabled: false,
    IsFluctuationExperimentEnabled: false,
    hideChartPanel: false,
    resizeEventHandler: noop,
    token: null,
    seriesColors: null,
    gridCriterionType: '',
    dateRange: null,
    perfMarker: null,
    serverContext: null,
    gridDataSource: null,
    defaultPrimaryMetric: null,
    scope: {},
  };

  constructor(props) {
    super(props);
    this.state = {};

    // dataSource and chartOptions are created only once during construction
    this.dataSource = this.getDataSource();
    this.chartOptions = this.getChartOptions();
  }

  getDataSource() {
    const {
      edm,
      jsdata,
      pageContext,
      state,
    } = this.props;

    return new InlineChartService({
      edm,
      jsdata,
      pageContext,
      state,
    });
  }

  /**
   * Creates the chartOptions object used for dependency injection of legacy components.
   * @param {Object} options - Dictionary of legacy components.
   * @returns {Object} chartOptions object to be passed in as an InlineChart prop.
   */
  getChartOptions() {
    const {
      adapter,
      addDateChangeListener,
      entityType,
      imagePath,
      instrumentation,
      isFluctuationsEnabled,
      IsFluctuationExperimentEnabled,
      pageContext,
      pathConfig,
      preferenceService,
      resizeChartListener,
      resizeEventHandler,
      state,
      store,
      token,
      seriesColors,
      gridCriterionType,
      hideChartPanel,
      defaultPrimaryMetric,
    } = this.props;

    const preferences = new InlineChartPreferences(
      store,
      pathConfig,
      adapter,
      preferenceService
    );

    return {
      isFluctuationsEnabled,
      isFluctuationsExperiementEnabled: IsFluctuationExperimentEnabled,
      hideChartPanel,
      defaultPrimaryMetric,
      fluctuationService: FluctuationService,
      entityType,
      preference: preferences,
      pageContext,
      instrumentation,
      token,
      pageState: state,
      addDateChangeListener,
      resizeChartListener,
      resizeEventHandler,
      imagePath,
      seriesColors,
      gridCriterionType,
    };
  }

  render() {
    const {
      i18n,
      filter,
      isShowChart,
      dateRange,
      perfMarker,
      serverContext,
      gridDataSource,
      scope,
    } = this.props;

    return (<InlineChart
      isShowChart={isShowChart}
      scope={scope}
      i18n={i18n}
      filter={filter}
      dataSource={this.dataSource}
      options={this.chartOptions}
      dateRange={dateRange}
      perfMarker={perfMarker}
      serverContext={serverContext}
      gridDataSource={gridDataSource}
    />);
  }
}
